<template>
  <nav class="header navbar navbar-expand-lg fixed-top">
    <div class="container-fluid px-0">
      <!-- Menu Icon -->
      <div
        ref="togglerEl"
        class="navbar-toggler d-flex d-lg-none align-items-center justify-content-center"
        type="button"
        aria-controls="navbarSupportedContent"
        :aria-expanded="isExpanded ? 'true' : 'false'"
        aria-label="Toggle navigation"
        @click="toggleSidebar"
      >
        <i :class="isExpanded ? 'km-close' : 'km-menu'" />
      </div>

      <!-- Logo -->
      <router-link to="/">
        <img
          class="logo"
          src="https://ik.imagekit.io/1pzxddx3qqv/KUMA_Official/image/logo.png"
          alt="logo"
          loading="lazy"
        />
      </router-link>

      <!-- Navbar -->
      <div
        ref="navbarCollapseEl"
        class="collapse navbar-collapse"
        id="navbarSupportedContent"
      >
        <ul class="navbar-nav ms-lg-auto p-3 p-lg-0">
          <li
            v-for="(item, idx) in navList"
            :key="idx"
            class="nav-item dropdown dropdown-hover mb-lg-0"
            :class="{
              'mb-4': idx !== navList.length - 1,
              'mb-0': idx === navList.length - 1,
            }"
          >
            <a
              class="nav-link dropdown-toggle d-flex align-items-center justify-content-lg-between justify-content-start"
              href="#"
              :id="`navbarDropdown-${idx}`"
              role="button"
              :data-bs-toggle="item.subList.length > 0 ? 'dropdown' : null"
              :aria-expanded="item.subList.length > 0 ? 'false' : null"
              @click.prevent="handleNavClick(item)"
            >
              <i :class="item.icon"></i>
              <span class="header-link-text">{{ t(item.titleKey) }}</span>
              <i v-if="item.subList.length > 0" class="km-arrow-down"></i>
            </a>

            <ul
              v-if="item.subList.length > 0"
              class="dropdown-menu dropdown-menu-end py-0 p-lg-3 px-3"
            >
              <li v-for="(subItem, subIdx) in item.subList" :key="subIdx">
                <router-link
                  class="dropdown-item"
                  :class="{ active: isActiveSubItem(subItem) }"
                  :to="subItem.link"
                  @click="closeSidebar"
                >
                  {{ t(subItem.titleKey) }}
                </router-link>
              </li>
            </ul>
          </li>

          <li><hr class="dropdown-divider" /></li>

          <!-- Language -->
          <li
            class="nav-item dropdown dropdown-hover"
            :class="{ show: isMobile }"
          >
            <a
              class="nav-link dropdown-toggle d-flex align-items-center justify-content-lg-between justify-content-start"
              href="#"
              id="localeNavbarDropdown"
              role="button"
              :data-bs-toggle="isMobile ? null : 'dropdown'"
              :aria-expanded="isMobile ? 'true' : 'false'"
              @click.prevent
            >
              <span class="header-link-text">
                {{
                  isMobile
                    ? t("km.common.page.list.language")
                    : currentLocale.toUpperCase()
                }}
              </span>
              <i class="km-arrow-right"></i>
            </a>

            <ul
              class="dropdown-menu dropdown-menu-end py-0 py-lg-2 px-3 px-lg-3"
              :class="{ show: isMobile }"
            >
              <li
                v-for="(localeItem, localeIdx) in locales"
                :key="localeIdx"
                @click="handleLocaleSwitch(localeItem)"
              >
                <a
                  class="dropdown-item"
                  :class="{ active: isActiveLocale(localeItem) }"
                  href="#"
                  @click.prevent
                >
                  {{ localeItem.toUpperCase() }}
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import Collapse from "bootstrap/js/dist/collapse";
import useLocale from "@/composables/useLocale";
import { useExternalLink } from "@/composables/useExternalLinks";
import useIsMobile from "@/composables/useIsMobile";

const { locale, availableLocales, t } = useI18n();
const { switchLocale } = useLocale();
const { openGoogleSheet } = useExternalLink();
const { isMobile } = useIsMobile();
const route = useRoute();

const currentLocale = computed(() => locale.value);

const togglerEl = ref(null);
const navbarCollapseEl = ref(null);
const collapseInstance = ref(null);
const isExpanded = ref(false);

const locales = computed(() =>
  availableLocales.filter((v) => ["en", "zh"].includes(v))
);

const getLastPathSegment = (path) => {
  return path.split("/").filter(Boolean).pop() || "";
};

const isActiveSubItem = (subItem) => {
  return getLastPathSegment(route.path) === getLastPathSegment(subItem.link);
};

const isActiveLocale = (localeItem) => {
  return localeItem === currentLocale.value;
};

const navList = ref([
  {
    titleKey: "km.common.page.list.launchSuite",
    icon: "km-xmark color-secondary",
    subList: [
      {
        titleKey: "km.common.page.subList.beginner",
        link: "/launchSuite/beginner",
      },
      {
        titleKey: "km.common.page.subList.advance",
        link: "/launchSuite/advance",
      },
      {
        titleKey: "km.common.page.subList.custom",
        link: "/launchSuite/custom",
      },
    ],
  },
  {
    titleKey: "km.common.page.list.systemCenter",
    icon: "km-square color-error",
    subList: [
      {
        titleKey: "km.common.page.subList.frontendOverview",
        link: "/systemCenter/frontendOverview",
      },
      {
        titleKey: "km.common.page.subList.backendConsole",
        link: "/systemCenter/backendConsole",
      },
      {
        titleKey: "km.common.page.subList.gameSetup",
        link: "/systemCenter/gameSetup",
      },
    ],
  },
  {
    titleKey: "km.common.page.list.kumaDna",
    icon: "km-kuma color-tertiary",
    subList: [
      {
        titleKey: "km.common.page.subList.KUMAOverview",
        link: "/kumaDNA/kumaOverview",
      },
      {
        titleKey: "km.common.page.subList.expoFootprints",
        link: "/kumaDNA/expoFootprints",
      },
    ],
  },
  {
    titleKey: "km.common.page.list.partnerHub",
    icon: "km-circle color-warning",
    subList: [
      {
        titleKey: "km.common.page.subList.partnerBrands",
        link: "/partnerHub/partnerBrands",
      },
      {
        titleKey: "km.common.page.subList.campaignPush",
        link: "/partnerHub/campaignPush",
      },
      {
        titleKey: "km.common.page.subList.tagStudio",
        link: "/partnerHub/tagStudio",
      },
    ],
  },
  {
    titleKey: "km.common.page.list.smartSupport",
    icon: "km-trangle color-info",
    action: "openGoogleSheet",
    subList: [],
  },
]);

const handleShown = () => {
  isExpanded.value = true;
};

const handleHidden = () => {
  isExpanded.value = false;
};

const toggleSidebar = () => {
  if (!isMobile.value || !collapseInstance.value) return;
  collapseInstance.value.toggle();
};

const closeSidebar = () => {
  if (!isMobile.value || !collapseInstance.value) return;
  collapseInstance.value.hide();
};

const handleLocaleSwitch = (localeItem) => {
  if (localeItem !== currentLocale.value) {
    switchLocale(localeItem);
  }
  closeSidebar();
};

const handleNavClick = (item) => {
  switch (item.action) {
    case "openGoogleSheet":
      openGoogleSheet();
      closeSidebar();
      break;
    default:
      break;
  }
};

onMounted(() => {
  if (!navbarCollapseEl.value) return;

  collapseInstance.value = new Collapse(navbarCollapseEl.value, {
    toggle: false,
  });

  navbarCollapseEl.value.addEventListener("shown.bs.collapse", handleShown);
  navbarCollapseEl.value.addEventListener("hidden.bs.collapse", handleHidden);

  isExpanded.value = navbarCollapseEl.value.classList.contains("show");
});

onBeforeUnmount(() => {
  if (!navbarCollapseEl.value) return;

  navbarCollapseEl.value.removeEventListener("shown.bs.collapse", handleShown);
  navbarCollapseEl.value.removeEventListener(
    "hidden.bs.collapse",
    handleHidden
  );
});
</script>
